import React, { useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import Pagination from "views/pages/components/Pagination";
import SearchComponent from "views/pages/listing/SearchComponent";
import helpers from "utils/helpers";
import {
  Spinner,
  Button,
  UncontrolledTooltip,
  Badge,
} from "reactstrap";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useAuth } from "context/AuthContext";
import { useTranslation } from "context/TranslationProvider";
import { useNavigate } from "react-router-dom";

function ListingModalTable({
  items = [],
  isItemsLoading,
  type,
  rotate,
  searchFrom,
  searchTo,
  isSelectedFrom,
  isSelectedTo,
  filteredCities = [],
  handleFromChange,
  handleToChange,
  handleFromCity,
  handleToCity,
  toggleFromTo,
  startDate,
  setStartDate,
  today,
  getSearchSenderListings,
  noCityMatchMessage,
}) {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const { permission } = useAuth();
  const listingPermission = permission?.permissions?.modules?.listings;
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const isRowIncomplete = (row) => {
    const fieldsToCheck = [
      row.fromPlace?.name,
      row.toPlace?.name,
      row.price || row?.baggageDetails?.price || row?.packageDetails?.price,
      row.date,
    ];

    return fieldsToCheck.some((field) => !field);
  };

  if (isItemsLoading) {
    return (
      <div className="center-loader">
        <div className="center-loader-content">
          <Spinner/>
          <p className="mt-3 text-muted">
            {t("common.loading", "Loading....")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <ToolkitProvider
      keyField="_id"
      data={items}
      key={language}
      search
      columns={[
        {
          dataField: "fromPlace.name",
          text: t("table.headers.from", "From"),
          sort: true,
        },
        {
          dataField: "toPlace.name",
          text: t("table.headers.to", "To"),
          sort: true,
        },
        {
          dataField: "price",
          text: t("table.headers.price", "Price"),
          formatter: (_, row) => {
            const details =
              row?.baggageDetails ||
              row?.packageDetails ||
              row?.listing?.packageDetails;
            if (!details?.price) return "-";

            const symbol =
              details?.currency?.symbol || helpers.DEFAULT_CURRENCY;

            return `${symbol}${details.price}`;
          },
        },
        {
          dataField: "date",
          text: t("page.orders.journeyDate", "Journey Date"),
          sort: true,
          formatter: (_, row) =>
            row?.date ? helpers.getFormattedDate(row.date, t) : "",
        },
        {
          dataField: "status",
          text: t("table.headers.status", "Status"),
          formatter: (cell) => {
            const statusKey = cell || "pending";

            const statusColor = helpers
              .getStatusClass(statusKey, "tableListing")
              .replace("status-", "") || "secondary";

            return (
              <Badge
                className="badge-lg"
                color={statusColor}
                onClick={(e) => e.stopPropagation()}
              >
                {t(`page.userListings.status.${statusKey}`)}
              </Badge>
            );
          },
        },
        {
          dataField: "createdAt",
          text: t("table.headers.createdAt", "Created At"),
          sort: true,
          formatter: (cell, row) =>
            row?.createdAt ? helpers.getFormattedDate(row.createdAt, t) : "",
        },
        {
          dataField: "actions",
          text: t("table.headers.action", "Action"),
          isDummyField: true,
          formatter: (_, row) => {
            const isDraft = row.status === "draft";
            const incomplete = isRowIncomplete(row);
            const canView = listingPermission?.read && !isDraft && !incomplete;

            return (
              <div className="d-flex align-items-center gap-1 w-100">
                <Button
                  id={`view-${row._id}`}
                  size="sm"
                  key={`view-${row._id}-${language}`}
                  disabled={!canView}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (!canView) return;

                    navigate(`/admin/listing-details/${type}/${row._id}`);
                  }}
                >
                  <VisibilityIcon fontSize="small" color={"primary"} />
                </Button>

                <UncontrolledTooltip
                  key={`view-tt-${row._id}-${language}`}
                  target={`view-${row._id}`}
                  trigger="hover"
                >
                  {!listingPermission?.read
                    ? t("tooltip.viewNotAllowed", "View not allowed")
                    : isDraft
                    ? t("tooltip.draftNotViewable", "Draft listing cannot be viewed")
                    : incomplete
                    ? t("tooltip.incompleteNotViewable", "Cannot view listing because some fields are missing")
                    : t("tooltip.viewListing", "View listing details")}
                </UncontrolledTooltip>
              </div>
            );
          },
        }
      ]}
    >
      {(props) => (
        <>
          <div className="d-flex justify-content-between my-3 px-2">
            <SearchComponent
              rotate={rotate}
              searchFrom={searchFrom}
              searchTo={searchTo}
              isSelectedFrom={isSelectedFrom}
              isSelectedTo={isSelectedTo}
              filteredCities={filteredCities}
              handleFromChange={handleFromChange}
              handleToChange={handleToChange}
              handleFromCity={handleFromCity}
              handleToCity={handleToCity}
              toggleFromTo={toggleFromTo}
              startDate={startDate}
              setStartDate={setStartDate}
              today={today}
              getSearchSenderListings={getSearchSenderListings}
              noCityMatchMessage={noCityMatchMessage}
            />
          </div>
          <>
            <div className="table-responsive table-responsive-scroll">
              <BootstrapTable
                {...props.baseProps}
                data={props.baseProps.data.slice(
                  currentPage * rowsPerPage,
                  currentPage * rowsPerPage + rowsPerPage
                )}
                bootstrap4
                bordered={false}
                noDataIndication={() => (
                  <div className="text-center text-slate-500 py-4 w-full">
                    {t("page.listing.noListings", "No Listings Found")}
                  </div>
                )}
                rowEvents={{
                  onClick: (e) => {
                    e.stopPropagation();
                  },
                }}
              />
            </div>
            <Pagination
              totalRows={props.baseProps.data.length}
              rowsPerPage={rowsPerPage}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
              onRowsPerPageChange={(size) => {
                setRowsPerPage(size);
                setCurrentPage(0);
              }}
              rowsPerPageOptions={[5, 10, 20]}
              t={t}
            />
          </>
        </>
      )}
    </ToolkitProvider>
  );
}

export default ListingModalTable;
